import { useNavigate } from 'react-router-dom'
import { CheckCircle, Download } from 'lucide-react'
import { candidateDeepDive } from '@/features/candidates/data/mockData'

const SKILL_THEMES = [
  'Revenue Leadership & Strategy',
  'Team Building & Scaling',
  'EMEA Market Expertise',
  'B2B SaaS Operations',
  'Data-Driven Decision Making',
]

const ROLE_THEMES = [
  'C-Level Revenue Leadership',
  'High-Growth SaaS Companies',
  'International Expansion Focus',
  'Hybrid/Remote-Friendly Culture',
]

const WHAT_NEXT = [
  { step: 1, title: 'Improved Matching', desc: 'Recruiters can now match you to roles with far greater precision.' },
  { step: 2, title: 'Targeted Outreach', desc: 'Expect only relevant opportunities aligned to your goals.' },
  { step: 3, title: 'Faster Process',    desc: 'Pre-screening is streamlined — less back and forth.' },
]

const PROFILE_QUALITY = 92

export const CandidateDeepDiveSummary = () => {
  const navigate = useNavigate()
  const { summary } = candidateDeepDive

  return (
    <div className="bg-[#02182B] min-h-screen">
      {/* Page header */}
      <div className="border-b border-[#233D4C]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Deep-Dive Complete!</h1>
            <p className="text-gray-400 text-sm mt-1">Your profile is optimised for matches</p>
          </div>
          <button className="flex items-center gap-1.5 bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2a4a5c] transition-colors">
            <Download size={13} /> Save to Profile
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col lg:flex-row gap-8">

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* Success card */}
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle size={20} className="text-green-400" />
              </div>
              <div>
                <p className="text-white font-bold text-lg">You're Now Match Ready!</p>
                <p className="text-gray-400 text-sm">Your profile has been saved and shared with recruiters.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-6 pt-3 border-t border-green-500/10 text-sm">
              <div>
                <span className="text-gray-400">Profile quality </span>
                <span className="text-green-400 font-bold">{PROFILE_QUALITY}%</span>
              </div>
              <div>
                <span className="text-gray-400">Match improvement </span>
                <span className="text-[#FD802E] font-bold">3×</span>
              </div>
              <div>
                <span className="text-gray-400">Status </span>
                <span className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded-full text-xs font-medium">Active</span>
              </div>
            </div>
          </div>

          {/* Skill themes */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
            <p className="text-xs uppercase text-gray-400 font-medium mb-3">Top Skill Themes</p>
            <div className="flex flex-wrap gap-2">
              {SKILL_THEMES.map((theme) => (
                <span
                  key={theme}
                  className="px-3 py-1.5 bg-[#FD802E]/10 text-[#FD802E] rounded-full text-sm font-medium border border-[#FD802E]/20"
                >
                  {theme}
                </span>
              ))}
            </div>
          </div>

          {/* Target role themes */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
            <p className="text-xs uppercase text-gray-400 font-medium mb-3">Target Role Themes</p>
            <ul className="space-y-2">
              {ROLE_THEMES.map((theme) => (
                <li key={theme} className="flex items-center gap-2 text-sm text-gray-300">
                  <span className="text-[#FD802E]">•</span>
                  {theme}
                </li>
              ))}
            </ul>
          </div>

          {/* Recruiter summary preview */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase text-gray-400 font-medium">Recruiter Summary Preview</p>
              <button className="flex items-center gap-1 text-gray-400 hover:text-[#FD802E] text-xs transition-colors">
                <Download size={11} /> Download
              </button>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              {summary.candidateId === 'layla-haddad'
                ? 'Layla is a seasoned revenue leader with deep EMEA expansion experience, strong enterprise SaaS credentials, and a track record of building high-performing commercial teams. She is actively exploring C-level and VP-level revenue roles in high-growth, international-first businesses based in Riyadh or Dubai.'
                : `Candidate is seeking ${summary.targetRoles.join(' or ')} roles in ${summary.locations.join(' or ')}. Key must-haves include ${summary.mustHaves.slice(0, 2).join(' and ')}.`
              }
            </p>
          </div>

          {/* What happens next */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
            <p className="text-xs uppercase text-gray-400 font-medium mb-4">What Happens Next</p>
            <div className="space-y-4">
              {WHAT_NEXT.map((item) => (
                <div key={item.step} className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#FD802E]/10 text-[#FD802E] text-xs font-bold rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    {item.step}
                  </span>
                  <div>
                    <p className="text-white font-semibold text-sm">{item.title}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Right sidebar */}
        <div className="w-full lg:w-72 lg:flex-shrink-0 space-y-4">

          {/* Profile status */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-3">
            <p className="text-xs uppercase text-gray-400 font-medium">Profile Status</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Status</span>
                <span className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded-full text-xs font-medium">Match Ready</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Completed</span>
                <span className="text-white text-xs">March 20, 2026</span>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-400">Profile Quality</span>
                  <span className="text-green-400 font-medium">{PROFILE_QUALITY}%</span>
                </div>
                <div className="w-full h-1.5 bg-[#2a4a5c] rounded-full overflow-hidden">
                  <div className="h-full bg-green-400 rounded-full" style={{ width: `${PROFILE_QUALITY}%` }} />
                </div>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="space-y-2">
            <button
              onClick={() => navigate('/')}
              className="w-full bg-[#FD802E] text-[#233D4C] font-semibold text-sm py-3 rounded-lg hover:bg-[#ff8f45] transition-colors"
            >
              Return to Candidate Portal
            </button>
            <button className="w-full flex items-center justify-center gap-1.5 bg-[#233D4C] border border-[#2a4a5c] text-white text-sm py-2.5 rounded-lg hover:border-[#FD802E]/40 transition-colors">
              <Download size={13} /> Download Summary
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}
